import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CURRENCY_META, SUPPORTED_CURRENCIES, type SupportedCurrency } from '../../store/slices/currencySlice';
import Button from '../common/Button';
import InlineStatus from '../common/InlineStatus';
import { getAuthRefreshState, subscribeAuthRefreshState } from '../../api/client';
import {
  downloadForm8949,
  downloadGenericExport,
  downloadTurboTaxExport,
  getTaxExportSummary,
  getTaxExportStatus,
  triggerBlobDownload,
  type TaxExportOperationStatus,
  type TaxExportSummary,
  type TaxExportSupportedCurrency,
} from '../../api/tax-export';
import {
  getSafeTaxExportYear,
  persistTaxExportSelectedYear,
  readPersistedTaxExportYear,
  TAX_EXPORT_YEAR_OPTIONS,
} from '../../lib/taxExportYear';
import type { TaxNextStepHandoffContext } from '../../lib/taxNextStepHandoff';
import TaxNextStepArrivalRail from './TaxNextStepArrivalRail';

type ExportActionKey = 'form8949' | 'turbotax' | 'generic';
type TaxExportBlockerCode = 'AUTH_SESSION_BLOCKED' | 'POPULATED_YEAR_DATA_BLOCKED';

export type { ExportActionKey };

export interface TaxExportPanelActionSnapshot {
  state: TaxExportCardStatus['state'] | null;
  updatedAt?: string;
  completedAt?: string;
  startedAt?: string;
  fileName?: string;
  message?: string;
  error?: string;
  appliedCurrency?: TaxExportSupportedCurrency;
  requestedCurrency?: TaxExportSupportedCurrency;
  blockerCode?: TaxExportBlockerCode;
}

export interface TaxExportPanelSnapshot {
  selectedYear: number;
  selectedCurrency: TaxExportSupportedCurrency;
  summary: TaxExportSummary | null;
  summaryLoading: boolean;
  summaryError: string | null;
  blockerCode?: TaxExportBlockerCode;
  actions: Partial<Record<ExportActionKey, TaxExportPanelActionSnapshot | null>>;
}

interface TaxExportBlocker {
  code: TaxExportBlockerCode;
  title: string;
  message: string;
  guidance: string;
}

interface TaxExportBlockedStatus {
  state: 'blocked';
  blockerCode: TaxExportBlockerCode;
  title: string;
  message: string;
  meta: string;
  format: ExportActionKey;
  userId: string;
  year: number;
  updatedAt: string;
  requestedCurrency?: TaxExportSupportedCurrency;
  appliedCurrency?: TaxExportSupportedCurrency;
  defaultCurrency?: TaxExportSupportedCurrency;
}

type TaxExportCardStatus = TaxExportOperationStatus | TaxExportBlockedStatus;
type ActionStatusState = Partial<Record<ExportActionKey, TaxExportCardStatus | null>>;

const ACTION_FORMATS: Record<ExportActionKey, ExportActionKey> = {
  form8949: 'form8949',
  turbotax: 'turbotax',
  generic: 'generic',
};
const ACTION_FILE_PREFIX: Record<ExportActionKey, string> = {
  form8949: 'Form 8949 CSV',
  turbotax: 'TurboTax TXF',
  generic: 'Generic CSV',
};

const ExportIcon = () => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 3v10" />
    <path d="M6.5 8.5 11 13l4.5-4.5" />
    <path d="M4 17.5h14" />
  </svg>
);

const EmptyStateIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="8" y="6" width="28" height="32" rx="4" />
    <path d="M15 16h14M15 22h14M15 28h8" />
  </svg>
);

function isSupportedCurrency(value: string | null | undefined): value is TaxExportSupportedCurrency {
  return SUPPORTED_CURRENCIES.includes(value as SupportedCurrency);
}

function formatCurrencySource(source?: string): string {
  switch (source) {
    case 'display_currency':
      return 'saved preference';
    case 'preferred_currency':
      return 'saved legacy preference';
    case 'usd_fallback':
      return 'USD fallback';
    default:
      return 'fallback';
  }
}

function getTaxExportErrorDetails(
  error: unknown,
  fallback: string,
): {
  message: string;
  status?: number;
  code?: string;
} {
  const authState = getAuthRefreshState();

  if (authState.status === 'sign_in_required') {
    return {
      message: authState.message ?? 'Session expired. Please sign in again.',
      status: 403,
      code: 'AUTH_SESSION_BLOCKED',
    };
  }

  const maybeError = error as {
    response?: {
      status?: number;
      data?: {
        error?: string;
        message?: string;
        errorCode?: string;
        code?: string;
      };
    };
    message?: string;
  };

  const status = maybeError.response?.status;
  const code = maybeError.response?.data?.errorCode ?? maybeError.response?.data?.code;
  const baseMessage =
    maybeError.response?.data?.error ??
    maybeError.response?.data?.message ??
    maybeError.message ??
    fallback;

  if ((status === 401 || status === 403) && !/sign in/i.test(baseMessage)) {
    return {
      message: `${baseMessage} Sign in again and retry this export.`,
      status,
      code: code ?? 'AUTH_SESSION_BLOCKED',
    };
  }

  return {
    message: baseMessage,
    status,
    code,
  };
}

function isTerminalState(state?: TaxExportCardStatus['state']): boolean {
  return state === 'ready' || state === 'failed' || state === 'empty' || state === 'blocked';
}

function ProofValueSkeleton({ widthClass = 'w-28' }: { widthClass?: string }) {
  return <div className={`h-4 rounded bg-gray-800 animate-pulse ${widthClass}`} aria-hidden="true" />;
}

function MetadataStack({
  rows,
}: {
  rows: Array<{ label: string; value: string; helper?: string }>;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-gray-800 bg-gray-950/50 p-3">
      {rows.map((row) => (
        <div key={row.label} className="space-y-1">
          <p className="text-[11px] uppercase tracking-wider text-gray-500">{row.label}</p>
          <p className="font-mono tabular-nums text-xs text-gray-200">{row.value}</p>
          {row.helper ? <p className="text-[11px] text-gray-500">{row.helper}</p> : null}
        </div>
      ))}
    </div>
  );
}

function ActionStatusMessage({
  action,
  status,
  selectedYear,
  selectedCurrency,
  onRetry,
}: {
  action: ExportActionKey;
  status: TaxExportCardStatus | null;
  selectedYear: number;
  selectedCurrency: TaxExportSupportedCurrency;
  onRetry: () => void;
}) {
  if (!status) {
    return <InlineStatus tone="neutral" message="Choose a year, then use this format card when you&apos;re ready." compact />;
  }

  if (status.state === 'blocked') {
    return (
      <InlineStatus
        tone="warning"
        title={status.title}
        message={status.message}
        meta={status.meta}
        compact
      />
    );
  }

  const defaultMessage = (() => {
    switch (status.state) {
      case 'preparing':
        return `Preparing dataset for ${selectedYear}…`;
      case 'generating':
        return `Generating ${ACTION_FILE_PREFIX[action]} in ${status.appliedCurrency ?? (action === 'generic' ? selectedCurrency : 'USD')}…`;
      case 'slow':
        return `Still working on the ${ACTION_FILE_PREFIX[action]} for ${selectedYear}. This large export is still active — there is no need to click again yet.`;
      case 'ready':
        return `${ACTION_FILE_PREFIX[action]} for ${selectedYear} in ${status.appliedCurrency ?? (action === 'generic' ? selectedCurrency : 'USD')} is ready.`;
      case 'failed':
        return status.error || 'Export failed. Please retry.';
      case 'empty':
        return `No realised disposals are available for ${selectedYear}.`;
      default:
        return '';
    }
  })();

  const message = status.message || defaultMessage;

  if (status.state === 'slow') {
    return (
      <InlineStatus
        tone="warning"
        title={`${ACTION_FILE_PREFIX[action]} is still running`}
        message={message}
        meta={`Export file currency: ${status.appliedCurrency ?? (action === 'generic' ? selectedCurrency : 'USD')}. Keep this tab open while the file finishes generating.`}
        compact
      />
    );
  }

  if (status.state === 'failed') {
    return (
      <InlineStatus
        tone="error"
        title={`${ACTION_FILE_PREFIX[action]} failed`}
        message={message}
        meta={`Year ${selectedYear} · Export file currency ${status.appliedCurrency ?? (action === 'generic' ? selectedCurrency : 'USD')} · Fix runtime issues before retrying.`}
        compact
        action={<Button type="button" onClick={onRetry} variant="outline" size="sm">Retry</Button>}
      />
    );
  }

  if (status.state === 'ready') {
    return (
      <InlineStatus
        tone="success"
        title={`${ACTION_FILE_PREFIX[action]} ready`}
        message={message}
        meta={[
          status.fileName ? `Downloaded file: ${status.fileName}` : null,
          `Export file currency: ${status.appliedCurrency ?? (action === 'generic' ? selectedCurrency : 'USD')}`,
        ].filter(Boolean).join(' · ')}
        compact
      />
    );
  }

  if (status.state === 'empty') {
    return <InlineStatus tone="neutral" title="Nothing to export yet" message={message} compact />;
  }

  return <InlineStatus tone="info" message={message} compact />;
}

export function TaxExportPanel({
  userId,
  activeDisplayCurrency,
  initialSelectedYear,
  handoff,
  onSnapshotChange,
  showArrivalRail = true,
}: {
  userId?: string;
  activeDisplayCurrency?: string;
  initialSelectedYear?: number;
  handoff?: TaxNextStepHandoffContext | null;
  onSnapshotChange?: (snapshot: TaxExportPanelSnapshot) => void;
  showArrivalRail?: boolean;
}) {
  const [selectedYear, setSelectedYear] = useState<number>(() =>
    getSafeTaxExportYear(initialSelectedYear ?? readPersistedTaxExportYear()),
  );
  const [selectedCurrency, setSelectedCurrency] = useState<TaxExportSupportedCurrency>('USD');
  const [summary, setSummary] = useState<TaxExportSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryBlocker, setSummaryBlocker] = useState<TaxExportBlocker | null>(null);
  const [loadingAction, setLoadingAction] = useState<ExportActionKey | null>(null);
  const [actionStatuses, setActionStatuses] = useState<ActionStatusState>({});
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const [authRefreshStatus, setAuthRefreshStatus] = useState(getAuthRefreshState);
  const pollTimersRef = useRef<Partial<Record<ExportActionKey, number>>>({});
  const exportProofRef = useRef<HTMLDivElement | null>(null);
  const exportCardsRef = useRef<HTMLDivElement | null>(null);

  const resolvedDisplayCurrency = isSupportedCurrency(activeDisplayCurrency)
    ? activeDisplayCurrency
    : null;
  const activeSummary = summary?.year === selectedYear ? summary : null;
  const hasMountedSummaryProof = Boolean(activeSummary);

  const buildAuthSessionBlocker = useCallback((): TaxExportBlocker => ({
    code: 'AUTH_SESSION_BLOCKED',
    title: 'Export blocked until you sign in again',
    message:
      authRefreshStatus.message ??
      'Your authenticated session is unavailable, so Pinecurl cannot prepare or download tax exports right now.',
    guidance: 'Sign in again, return to this export tab, and keep the selected year visible while you retry.',
  }), [authRefreshStatus.message]);

  const buildPopulatedYearBlocker = useCallback((): TaxExportBlocker => ({
    code: 'POPULATED_YEAR_DATA_BLOCKED',
    title: 'No safe populated disposal year is available',
    message: `Pinecurl could not confirm a safe populated disposal dataset for ${selectedYear} here yet.`,
    guidance: 'Choose another year with realised disposals or retry after a populated-year dataset is available.',
  }), [selectedYear]);

  const resolveBlockerFromError = useCallback((error: unknown): TaxExportBlocker | null => {
    const details = getTaxExportErrorDetails(error, 'Unable to load tax export details right now.');

    if (
      !userId ||
      details.code === 'AUTH_SESSION_BLOCKED' ||
      details.status === 401 ||
      details.status === 403 ||
      authRefreshStatus.status === 'sign_in_required'
    ) {
      return buildAuthSessionBlocker();
    }

    if (details.code === 'POPULATED_YEAR_DATA_BLOCKED') {
      return buildPopulatedYearBlocker();
    }

    return null;
  }, [authRefreshStatus.status, buildAuthSessionBlocker, buildPopulatedYearBlocker, userId]);

  const clearPoll = useCallback((action: ExportActionKey) => {
    const timerId = pollTimersRef.current[action];
    if (timerId) {
      window.clearInterval(timerId);
      delete pollTimersRef.current[action];
    }
  }, []);

  const clearAllPolls = useCallback(() => {
    (Object.keys(ACTION_FORMATS) as ExportActionKey[]).forEach((action) => clearPoll(action));
  }, [clearPoll]);

  useEffect(() => subscribeAuthRefreshState(setAuthRefreshStatus), []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    persistTaxExportSelectedYear(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    if (initialSelectedYear === undefined) {
      return;
    }

    const nextYear = getSafeTaxExportYear(initialSelectedYear);
    if (nextYear === selectedYear) {
      return;
    }

    setSelectedYear(nextYear);
    clearAllPolls();
    setActionStatuses({});
    setSummaryBlocker(null);
    setSummaryError(null);
  }, [clearAllPolls, initialSelectedYear, selectedYear]);

  const loadSummary = useCallback(async () => {
    if (!userId) {
      setSummary(null);
      setSummaryError(null);
      setSummaryBlocker(buildAuthSessionBlocker());
      return;
    }

    setSummaryLoading(true);
    setSummaryError(null);
    setSummaryBlocker(null);

    try {
      const nextSummary = await getTaxExportSummary(userId, selectedYear);
      setSummary(nextSummary);
      setSummaryBlocker(null);
    } catch (error) {
      const blocker = resolveBlockerFromError(error);
      if (blocker && !hasMountedSummaryProof) {
        setSummary(null);
        setSummaryBlocker(blocker);
        setSummaryError(null);
      } else if (blocker) {
        setSummaryBlocker(blocker);
        setSummaryError(null);
      } else {
        if (!hasMountedSummaryProof) {
          setSummary(null);
        }
        setSummaryBlocker(null);
        setSummaryError(getTaxExportErrorDetails(error, 'Unable to load tax export details right now.').message);
      }
    } finally {
      setSummaryLoading(false);
    }
  }, [buildAuthSessionBlocker, hasMountedSummaryProof, resolveBlockerFromError, selectedYear, userId]);

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    return () => {
      clearAllPolls();
    };
  }, [clearAllPolls]);

  useEffect(() => {
    const nextCurrency = resolvedDisplayCurrency ?? activeSummary?.defaultCurrency ?? null;
    if (!currencyTouched && nextCurrency) {
      setSelectedCurrency(nextCurrency);
    }
  }, [activeSummary?.defaultCurrency, currencyTouched, resolvedDisplayCurrency]);

  useEffect(() => {
    if (handoff?.tab !== 'export') {
      return;
    }

    const focusTarget =
      handoff.focus === 'export-cards'
        ? exportCardsRef.current
        : exportProofRef.current;

    if (!focusTarget) {
      return;
    }

    window.requestAnimationFrame(() => {
      if (typeof focusTarget.scrollIntoView === 'function') {
        focusTarget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      focusTarget.focus();
    });
  }, [handoff]);

  const savedDisplayCurrency = activeSummary?.effectiveDisplayCurrency ?? resolvedDisplayCurrency ?? activeSummary?.defaultCurrency ?? selectedCurrency;
  const savedCurrencySource = activeSummary?.currencySource ? formatCurrencySource(activeSummary.currencySource) : null;
  const monetaryBaseCurrency = activeSummary?.monetaryBaseCurrency ?? 'USD';
  const genericRequestedCurrency = actionStatuses.generic?.requestedCurrency ?? selectedCurrency;
  const genericExportCurrency = actionStatuses.generic?.appliedCurrency
    ?? (activeSummary?.displayCurrencyConversionAvailable === false && genericRequestedCurrency !== monetaryBaseCurrency
      ? monetaryBaseCurrency
      : genericRequestedCurrency);
  const genericFallsBackToBaseCurrency = genericExportCurrency === monetaryBaseCurrency && genericRequestedCurrency !== monetaryBaseCurrency;
  const sharedBlocker = authRefreshStatus.status === 'sign_in_required'
    ? buildAuthSessionBlocker()
    : summaryBlocker;

  const buildBlockedStatus = useCallback((
    action: ExportActionKey,
    blocker: TaxExportBlocker,
  ): TaxExportBlockedStatus => ({
    state: 'blocked',
    blockerCode: blocker.code,
    title: blocker.title,
    message: blocker.message,
    meta: `Blocker: ${blocker.code} · Year ${selectedYear} · Export file currency ${action === 'generic' ? genericExportCurrency : monetaryBaseCurrency}. ${blocker.guidance}`,
    format: ACTION_FORMATS[action],
    userId: userId ?? '',
    year: selectedYear,
    updatedAt: new Date().toISOString(),
    requestedCurrency: action === 'generic' ? genericRequestedCurrency : undefined,
    appliedCurrency: action === 'generic' ? genericExportCurrency : monetaryBaseCurrency,
    defaultCurrency: savedDisplayCurrency,
  }), [genericExportCurrency, genericRequestedCurrency, monetaryBaseCurrency, savedDisplayCurrency, selectedYear, userId]);

  const exportDisabled = summaryLoading || Boolean(summaryError) || Boolean(sharedBlocker) || !activeSummary?.hasDisposals || !userId;
  const exportDisabledReason =
    summaryLoading
      ? 'Loading the disposal proof for this year first.'
      : sharedBlocker
      ? `${sharedBlocker.code}: ${sharedBlocker.message}`
      : summaryError
      ? 'Retry the disposal summary before exporting.'
      : !userId
      ? 'Sign in again to export tax files.'
      : activeSummary?.hasDisposals === false
      ? 'No realised disposals are available for this year.'
      : undefined;

  const genericCurrencyLabel = useMemo(() => {
    return `${selectedCurrency} · ${CURRENCY_META[selectedCurrency].name}`;
  }, [selectedCurrency]);

  const buildPendingStatus = useCallback((action: ExportActionKey): TaxExportOperationStatus => ({
    state: 'preparing',
    format: ACTION_FORMATS[action],
    userId: userId ?? '',
    year: selectedYear,
    updatedAt: new Date().toISOString(),
    startedAt: new Date().toISOString(),
    requestedCurrency: action === 'generic' ? selectedCurrency : undefined,
    appliedCurrency: action === 'generic' ? genericExportCurrency : monetaryBaseCurrency,
    defaultCurrency: activeSummary?.defaultCurrency ?? resolvedDisplayCurrency ?? selectedCurrency,
    sharedDatasetReady: false,
    slowThresholdMs: 0,
    message:
      action === 'generic'
        ? `Preparing dataset for ${selectedYear} · ${genericExportCurrency}…`
        : `Preparing dataset for ${selectedYear} · ${monetaryBaseCurrency}…`,
  }), [
    activeSummary?.defaultCurrency,
    genericExportCurrency,
    monetaryBaseCurrency,
    resolvedDisplayCurrency,
    selectedCurrency,
    selectedYear,
    userId,
  ]);

  const pollStatus = useCallback(async (action: ExportActionKey) => {
    if (!userId) {
      const blockedStatus = buildBlockedStatus(action, buildAuthSessionBlocker());
      setActionStatuses((current) => ({ ...current, [action]: blockedStatus }));
      return blockedStatus;
    }

    try {
      const snapshot = await getTaxExportStatus(
        userId,
        selectedYear,
        action === 'generic' ? selectedCurrency : undefined,
      );
      const nextStatus = snapshot.exports[ACTION_FORMATS[action]] ?? null;
      if (nextStatus) {
        setActionStatuses((current) => ({ ...current, [action]: nextStatus }));
        if (isTerminalState(nextStatus.state)) {
          clearPoll(action);
        }
      }
      return nextStatus;
    } catch (error) {
      const blocker = resolveBlockerFromError(error);
      if (blocker) {
        const blockedStatus = buildBlockedStatus(action, blocker);
        setActionStatuses((current) => ({ ...current, [action]: blockedStatus }));
        clearPoll(action);
        return blockedStatus;
      }

      return null;
    }
  }, [
    buildAuthSessionBlocker,
    buildBlockedStatus,
    clearPoll,
    resolveBlockerFromError,
    selectedCurrency,
    selectedYear,
    userId,
  ]);

  const startPolling = useCallback((action: ExportActionKey) => {
    clearPoll(action);
    void pollStatus(action);
    pollTimersRef.current[action] = window.setInterval(() => {
      void pollStatus(action);
    }, 1500);
  }, [clearPoll, pollStatus]);

  const handleDownload = useCallback(async (action: ExportActionKey) => {
    const blocker = sharedBlocker ?? (!userId ? buildAuthSessionBlocker() : null);
    if (blocker) {
      setActionStatuses((current) => ({ ...current, [action]: buildBlockedStatus(action, blocker) }));
      return;
    }

    if (!userId) {
      return;
    }

    clearPoll(action);
    setLoadingAction(action);
    setActionStatuses((current) => ({ ...current, [action]: buildPendingStatus(action) }));
    startPolling(action);

    try {
      if (action === 'form8949') {
        const result = await downloadForm8949(userId, selectedYear);
        triggerBlobDownload(result.blob, result.fileName ?? `form-8949-${selectedYear}.csv`);
        setActionStatuses((current) => ({
          ...current,
          form8949: {
            ...(current.form8949 && current.form8949.state !== 'blocked' ? current.form8949 : buildPendingStatus('form8949')),
            state: result.exportState ?? 'ready',
            completedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            disposalCount: result.disposalCount ?? activeSummary?.disposalCount,
            hasDisposals: true,
            fileName: result.fileName ?? `form-8949-${selectedYear}.csv`,
            sharedDatasetReady: true,
            appliedCurrency: result.appliedCurrency ?? monetaryBaseCurrency,
            message: `Form 8949 CSV for ${selectedYear} in ${result.appliedCurrency ?? monetaryBaseCurrency} is ready.`,
          },
        }));
        clearPoll('form8949');
        return;
      }

      if (action === 'turbotax') {
        const result = await downloadTurboTaxExport(userId, selectedYear);
        triggerBlobDownload(result.blob, result.fileName ?? `turbotax-${selectedYear}.txf`);
        setActionStatuses((current) => ({
          ...current,
          turbotax: {
            ...(current.turbotax && current.turbotax.state !== 'blocked' ? current.turbotax : buildPendingStatus('turbotax')),
            state: result.exportState ?? 'ready',
            completedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            disposalCount: result.disposalCount ?? activeSummary?.disposalCount,
            hasDisposals: true,
            fileName: result.fileName ?? `turbotax-${selectedYear}.txf`,
            sharedDatasetReady: true,
            appliedCurrency: result.appliedCurrency ?? monetaryBaseCurrency,
            message: `TurboTax TXF for ${selectedYear} in ${result.appliedCurrency ?? monetaryBaseCurrency} is ready.`,
          },
        }));
        clearPoll('turbotax');
        return;
      }

      const result = await downloadGenericExport(userId, selectedYear, selectedCurrency);
      const appliedCurrency = result.appliedCurrency ?? selectedCurrency;
      triggerBlobDownload(result.blob, result.fileName ?? `capital-gains-${selectedYear}-${appliedCurrency}.csv`);
      setActionStatuses((current) => ({
        ...current,
        generic: {
          ...(current.generic && current.generic.state !== 'blocked' ? current.generic : buildPendingStatus('generic')),
          state: result.exportState ?? 'ready',
          completedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          disposalCount: result.disposalCount ?? activeSummary?.disposalCount,
          hasDisposals: true,
          requestedCurrency: selectedCurrency,
          appliedCurrency,
          fileName: result.fileName ?? `capital-gains-${selectedYear}-${appliedCurrency}.csv`,
          sharedDatasetReady: true,
          message: `Generic CSV for ${selectedYear} in ${appliedCurrency} is ready.`,
        },
      }));
    } catch (error) {
      const resolvedBlocker = resolveBlockerFromError(error);
      if (resolvedBlocker) {
        setActionStatuses((current) => ({ ...current, [action]: buildBlockedStatus(action, resolvedBlocker) }));
        clearPoll(action);
      } else {
        const status = await pollStatus(action);
        if (!status || status.state !== 'failed') {
          const details = getTaxExportErrorDetails(error, 'Download failed. Please retry.');
          setActionStatuses((current) => ({
            ...current,
            [action]: (() => {
              const previousStatus = current[action];
              const previousOperationStatus =
                previousStatus && previousStatus.state !== 'blocked'
                  ? previousStatus
                  : null;

              return {
                ...(previousOperationStatus ?? buildPendingStatus(action)),
              state: 'failed',
              completedAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              error: details.message,
              errorCode: details.code === 'EXPORT_RUNTIME_FAILED' ? details.code : previousOperationStatus?.errorCode,
              message: details.message,
              sharedDatasetReady: previousOperationStatus?.sharedDatasetReady ?? false,
              slowThresholdMs: previousOperationStatus?.slowThresholdMs ?? 0,
            };
            })(),
          }));
        }
        clearPoll(action);
      }
    } finally {
      setLoadingAction(null);
    }
  }, [
    activeSummary?.disposalCount,
    buildAuthSessionBlocker,
    buildBlockedStatus,
    buildPendingStatus,
    clearPoll,
    monetaryBaseCurrency,
    pollStatus,
    resolveBlockerFromError,
    selectedCurrency,
    selectedYear,
    sharedBlocker,
    startPolling,
    userId,
  ]);

  const proofRows = [
    {
      label: 'Selected year',
      content: <span className="font-mono tabular-nums text-gray-200">{selectedYear}</span>,
    },
    {
      label: 'Disposal proof',
      content: summaryLoading ? (
        hasMountedSummaryProof ? (
          <div className="space-y-1">
            <p className="text-sm text-gray-200">
              <span className="font-mono tabular-nums">{activeSummary!.disposalCount}</span>{' '}
              disposal event{activeSummary!.disposalCount === 1 ? '' : 's'}
            </p>
            <p className="text-xs text-gray-500">Checking year proof…</p>
          </div>
        ) : (
          <div className="space-y-2">
            <ProofValueSkeleton widthClass="w-40" />
            <ProofValueSkeleton widthClass="w-56" />
          </div>
        )
      ) : activeSummary ? (
        <div className="space-y-1">
          <p className="text-sm text-gray-200">
            <span className="font-mono tabular-nums">{activeSummary!.disposalCount}</span>{' '}
            disposal event{activeSummary!.disposalCount === 1 ? '' : 's'}
          </p>
          <p className="text-xs text-gray-500">
            {sharedBlocker
              ? `Showing the last confirmed proof while rechecking is blocked (${sharedBlocker.code}).`
              : summaryError
              ? 'Showing the last confirmed proof while Pinecurl rechecks this year.'
              : activeSummary.hasDisposals
              ? 'Shared disposal dataset is ready to feed all three export formats.'
              : 'No realised disposals are available for this selected year.'}
          </p>
        </div>
      ) : sharedBlocker ? (
        <div className="space-y-1">
          <p className="font-mono tabular-nums text-xs text-amber-300">{sharedBlocker.code}</p>
          <p className="text-sm text-gray-200">{sharedBlocker.message}</p>
        </div>
      ) : summaryError ? (
        <span className="text-sm text-red-300">{summaryError}</span>
      ) : (
        <span className="text-sm text-gray-400">Select a year to inspect exportable disposals.</span>
      ),
    },
    {
      label: 'Display currency',
      content: summaryLoading ? (
        hasMountedSummaryProof ? (
          <div className="space-y-1">
            <p className="font-mono tabular-nums text-gray-200">{savedDisplayCurrency}</p>
            <p className="text-xs text-gray-500">Checking saved display currency…</p>
          </div>
        ) : (
          <ProofValueSkeleton widthClass="w-28" />
        )
      ) : (
        <div className="space-y-1">
          <p className="font-mono tabular-nums text-gray-200">{savedDisplayCurrency}</p>
          <p className="text-xs text-gray-500">
            {sharedBlocker && hasMountedSummaryProof
              ? `Showing the last confirmed display currency while rechecking is blocked (${sharedBlocker.code}).`
              : summaryError && hasMountedSummaryProof
              ? 'Showing the last confirmed display currency while Pinecurl rechecks this year.'
              : savedCurrencySource
              ? savedCurrencySource
              : 'saved preference unavailable'}
          </p>
        </div>
      ),
    },
    {
      label: 'Base currency',
      content: summaryLoading ? (
        hasMountedSummaryProof ? (
          <div className="space-y-1">
            <p className="font-mono tabular-nums text-gray-200">{monetaryBaseCurrency}</p>
            <p className="text-xs text-gray-500">Rechecking base-currency proof…</p>
          </div>
        ) : (
          <ProofValueSkeleton widthClass="w-24" />
        )
      ) : (
        <div className="space-y-1">
          <p className="font-mono tabular-nums text-gray-200">{monetaryBaseCurrency}</p>
          <p className="text-xs text-gray-500">
            {sharedBlocker && hasMountedSummaryProof
              ? `Showing the last confirmed base currency while rechecking is blocked (${sharedBlocker.code}).`
              : summaryError && hasMountedSummaryProof
              ? 'Showing the last confirmed base currency while Pinecurl rechecks this year.'
              : 'Raw/base export currency for fixed-USD formats.'}
          </p>
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (!onSnapshotChange) {
      return;
    }

    const snapshot: TaxExportPanelSnapshot = {
      selectedYear,
      selectedCurrency,
      summary: activeSummary,
      summaryLoading,
      summaryError,
      blockerCode: sharedBlocker?.code,
      actions: (Object.keys(ACTION_FORMATS) as ExportActionKey[]).reduce<
        Partial<Record<ExportActionKey, TaxExportPanelActionSnapshot | null>>
      >((accumulator, action) => {
        const status =
          actionStatuses[action]
          ?? (sharedBlocker ? buildBlockedStatus(action, sharedBlocker) : null);

        accumulator[action] = status
          ? {
            state: status.state,
            updatedAt: status.updatedAt,
            completedAt: status.state !== 'blocked' ? status.completedAt : undefined,
            startedAt: status.state !== 'blocked' ? status.startedAt : undefined,
            fileName: status.state !== 'blocked' ? status.fileName : undefined,
            message: status.message,
            error: status.state !== 'blocked' ? status.error : undefined,
            appliedCurrency: status.appliedCurrency,
            requestedCurrency: status.requestedCurrency,
            blockerCode: status.state === 'blocked' ? status.blockerCode : undefined,
          }
          : null;
        return accumulator;
      }, {}),
    };

    onSnapshotChange(snapshot);
  }, [
    actionStatuses,
    activeSummary,
    buildBlockedStatus,
    onSnapshotChange,
    selectedCurrency,
    selectedYear,
    sharedBlocker,
    summaryError,
    summaryLoading,
  ]);

  return (
    <section className="space-y-6">
      {showArrivalRail && handoff?.tab === 'export' ? (
        <TaxNextStepArrivalRail
          handoff={handoff}
          title="Mount export proof before downloading"
          message={handoff.proofLabel}
        />
      ) : null}

      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-blue-500/30 bg-blue-500/10 text-blue-400">
          <ExportIcon />
        </div>
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-white">Export</h2>
          <p className="text-sm text-gray-400">
            Download your selected year&apos;s realised disposals for filing software import or accountant review.
          </p>
          <p className="text-xs text-gray-500">
            Display currency:{' '}
            <span className="font-mono tabular-nums text-gray-300">{savedDisplayCurrency}</span>
            {savedCurrencySource ? ` · ${savedCurrencySource}` : ''}
          </p>
        </div>
      </div>

      <div className="rounded-xl border border-gray-800 bg-gray-900 p-4 md:p-6 space-y-6">
        <div className="grid gap-4 md:grid-cols-[minmax(0,220px)_1fr] md:items-start">
          <div className="space-y-2">
            <label htmlFor="tax-export-year" className="block text-xs font-medium uppercase tracking-wider text-gray-400">
              Filing year
            </label>
            <select
              id="tax-export-year"
              value={selectedYear}
              onChange={(event) => {
                setSelectedYear(Number(event.target.value));
                clearAllPolls();
                setActionStatuses({});
                setSummaryBlocker(null);
                setSummaryError(null);
              }}
              className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 font-mono tabular-nums focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
            >
              {TAX_EXPORT_YEAR_OPTIONS.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <div
            ref={exportProofRef}
            tabIndex={-1}
            className="rounded-xl border border-gray-800 bg-gray-950/50 p-4 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          >
            <p className="text-xs uppercase tracking-wider text-gray-400">Disposal proof shell</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {proofRows.map((row) => (
                <div key={row.label} className="rounded-lg border border-gray-800 bg-gray-900/70 p-3">
                  <p className="text-[11px] uppercase tracking-wider text-gray-500">{row.label}</p>
                  <div className="mt-2">{row.content}</div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-gray-500">
              Form 8949, TurboTax, and Generic CSV all derive from this selected-year disposal dataset proof.
            </p>
          </div>
        </div>

        {summaryError && !sharedBlocker && !hasMountedSummaryProof && (
          <InlineStatus
            tone="error"
            title="Tax export summary unavailable"
            message={summaryError}
            action={<Button type="button" onClick={() => void loadSummary()} variant="outline" size="sm">Retry summary</Button>}
          />
        )}

        <div
          ref={exportCardsRef}
          tabIndex={-1}
          className="grid gap-4 lg:grid-cols-3 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
        >
          <div className="rounded-xl border border-gray-800 bg-gray-950/40 p-4 space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-white">Form 8949 CSV</p>
              <p className="text-sm text-gray-400">
                IRS-style disposal rows with acquisition dates, proceeds, basis, and short/long-term labels.
              </p>
            </div>
            <MetadataStack
              rows={[
                {
                  label: 'Display currency',
                  value: `${savedDisplayCurrency}${savedCurrencySource ? ` · ${savedCurrencySource}` : ''}`,
                },
                {
                  label: 'Export file currency',
                  value: `${actionStatuses.form8949?.appliedCurrency ?? monetaryBaseCurrency} · fixed for this format`,
                },
                {
                  label: 'Base currency',
                  value: `${monetaryBaseCurrency} · raw/base export values`,
                },
              ]}
            />
            <Button
              type="button"
              onClick={() => void handleDownload('form8949')}
              disabled={exportDisabled || loadingAction === 'form8949' || actionStatuses.form8949?.state === 'slow'}
              variant="outline"
              isLoading={loadingAction === 'form8949'}
              loadingLabel={actionStatuses.form8949?.state === 'generating' ? 'Generating…' : 'Preparing…'}
              disabledReason={actionStatuses.form8949?.state === 'slow' ? 'This export is still running — there is no need to click again yet.' : exportDisabledReason}
              fullWidth
            >
              Form 8949 CSV
            </Button>
            <div className="min-h-[120px]">
              {activeSummary?.hasDisposals === false ? (
                <InlineStatus tone="neutral" title="No disposal events" message="No disposal events are available to export for this year." compact />
              ) : (
                <ActionStatusMessage
                  action="form8949"
                  status={actionStatuses.form8949 ?? (sharedBlocker ? buildBlockedStatus('form8949', sharedBlocker) : null)}
                  selectedYear={selectedYear}
                  selectedCurrency={selectedCurrency}
                  onRetry={() => void handleDownload('form8949')}
                />
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-800 bg-gray-950/40 p-4 space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-white">TurboTax TXF</p>
              <p className="text-sm text-gray-400">
                Download a TXF file from the same disposal set for direct TurboTax desktop import.
              </p>
            </div>
            <MetadataStack
              rows={[
                {
                  label: 'Display currency',
                  value: `${savedDisplayCurrency}${savedCurrencySource ? ` · ${savedCurrencySource}` : ''}`,
                },
                {
                  label: 'Export file currency',
                  value: `${actionStatuses.turbotax?.appliedCurrency ?? monetaryBaseCurrency} · fixed for this format`,
                },
                {
                  label: 'Base currency',
                  value: `${monetaryBaseCurrency} · raw/base export values`,
                },
              ]}
            />
            <Button
              type="button"
              onClick={() => void handleDownload('turbotax')}
              disabled={exportDisabled || loadingAction === 'turbotax' || actionStatuses.turbotax?.state === 'slow'}
              variant="outline"
              isLoading={loadingAction === 'turbotax'}
              loadingLabel={actionStatuses.turbotax?.state === 'generating' ? 'Generating…' : 'Preparing…'}
              disabledReason={actionStatuses.turbotax?.state === 'slow' ? 'This export is still running — there is no need to click again yet.' : exportDisabledReason}
              fullWidth
            >
              TurboTax TXF
            </Button>
            <div className="min-h-[120px]">
              {activeSummary?.hasDisposals === false ? (
                <InlineStatus tone="neutral" title="TurboTax export unavailable" message="TurboTax export stays disabled until the selected year has disposals." compact />
              ) : (
                <ActionStatusMessage
                  action="turbotax"
                  status={actionStatuses.turbotax ?? (sharedBlocker ? buildBlockedStatus('turbotax', sharedBlocker) : null)}
                  selectedYear={selectedYear}
                  selectedCurrency={selectedCurrency}
                  onRetry={() => void handleDownload('turbotax')}
                />
              )}
            </div>
          </div>

          <div className="rounded-xl border border-gray-800 bg-gray-950/40 p-4 space-y-4">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-white">Generic CSV</p>
              <p className="text-sm text-gray-400">
                Export a currency-aware capital gains CSV for accountants or non-TurboTax workflows.
              </p>
            </div>
            <MetadataStack
              rows={[
                {
                  label: 'Display currency',
                  value: `${savedDisplayCurrency}${savedCurrencySource ? ` · ${savedCurrencySource}` : ''}`,
                },
                {
                  label: 'Requested currency',
                  value: genericRequestedCurrency,
                },
                {
                  label: 'Export file currency',
                  value: genericFallsBackToBaseCurrency
                    ? `${genericExportCurrency} · FX unavailable fallback`
                    : genericExportCurrency,
                },
                {
                  label: 'Base currency',
                  value: `${monetaryBaseCurrency} · raw/base export values`,
                },
              ]}
            />
            <div className="space-y-2">
              <label htmlFor="tax-export-currency" className="block text-xs font-medium uppercase tracking-wider text-gray-400">
                Requested currency
              </label>
              <select
                id="tax-export-currency"
                value={selectedCurrency}
                onChange={(event) => {
                  setCurrencyTouched(true);
                  setSelectedCurrency(event.target.value as TaxExportSupportedCurrency);
                  clearPoll('generic');
                  setActionStatuses((current) => ({ ...current, generic: null }));
                }}
                className="w-full rounded-lg border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 font-mono tabular-nums focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30"
              >
                {SUPPORTED_CURRENCIES.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 font-mono tabular-nums">{genericCurrencyLabel}</p>
              {genericFallsBackToBaseCurrency && (
                <InlineStatus
                  tone="warning"
                  title={`Generic CSV will stay in ${monetaryBaseCurrency}`}
                  message={`FX conversion is unavailable right now, so the export file currency will remain base currency ${monetaryBaseCurrency} until rates recover.`}
                  compact
                />
              )}
            </div>
            <Button
              type="button"
              onClick={() => void handleDownload('generic')}
              disabled={exportDisabled || loadingAction === 'generic' || actionStatuses.generic?.state === 'slow'}
              variant="outline"
              isLoading={loadingAction === 'generic'}
              loadingLabel={actionStatuses.generic?.state === 'generating' ? 'Generating…' : 'Preparing…'}
              disabledReason={actionStatuses.generic?.state === 'slow' ? 'This export is still running — there is no need to click again yet.' : exportDisabledReason}
              fullWidth
            >
              Generic CSV
            </Button>
            <div className="min-h-[120px]">
              {activeSummary?.hasDisposals === false ? (
                <InlineStatus tone="neutral" title="Generic export unavailable" message="Choose another year or create realised disposals before exporting." compact />
              ) : (
                <ActionStatusMessage
                  action="generic"
                  status={actionStatuses.generic ?? (sharedBlocker ? buildBlockedStatus('generic', sharedBlocker) : null)}
                  selectedYear={selectedYear}
                  selectedCurrency={selectedCurrency}
                  onRetry={() => void handleDownload('generic')}
                />
              )}
            </div>
          </div>
        </div>

        {activeSummary?.hasDisposals === false && (
          <div className="rounded-xl border border-gray-800 bg-gray-950/50 px-6 py-8 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-900 text-gray-600">
              <EmptyStateIcon />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-200">No realised disposals for {selectedYear}</h3>
            <p className="mt-2 text-sm text-gray-400">
              Export files are created from realised disposals only. Review your lots or run harvesting/simulation flows first.
            </p>
            <div className="mt-4 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link to="/tax-optimisation?tab=harvesting" className="btn btn--ghost text-sm">
                Review harvesting
              </Link>
              <Link to="/tax-optimisation?tab=simulator" className="text-sm text-white/60 underline-offset-4 transition-colors hover:text-white hover:underline">
                Open simulator
              </Link>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-gray-800 bg-gray-950/50 p-4 text-xs text-gray-500">
          Exports are informational only. Verify the selected year and totals before filing, and consult a qualified tax professional for jurisdiction-specific advice.
        </div>
      </div>
    </section>
  );
}
